import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { API_ENDPOINTS } from '../config/constants';
import '../styles/event-survey.css';

// ─── Question renderer helpers ────────────────────────────────────────────────

/**
 * Single question input — returns the appropriate widget for each question_type.
 * answers   : Map<questionId, string | number | number[]>
 * onChange  : (questionId, value) => void
 */
function QuestionInput({ question, answers, onChange }) {
  const { id, question_type, options = [], rating_max = 5, rating_label_low, rating_label_high } = question;
  const current = answers[id];

  switch (question_type) {
    case 'multiple_choice':
    case 'dropdown':
      return (
        <div className="es-options-list">
          {options.map(opt => (
            <label key={opt.id} className={`es-radio-label ${current === opt.id ? 'es-selected' : ''}`}>
              <input
                type="radio"
                name={`q_${id}`}
                value={opt.id}
                checked={current === opt.id}
                onChange={() => onChange(id, opt.id)}
              />
              <span className="es-radio-custom" />
              <span>{opt.option_text}</span>
            </label>
          ))}
        </div>
      );

    case 'checkbox':
      return (
        <div className="es-options-list">
          {options.map(opt => {
            const checked = Array.isArray(current) && current.includes(opt.id);
            return (
              <label key={opt.id} className={`es-checkbox-label ${checked ? 'es-selected' : ''}`}>
                <input
                  type="checkbox"
                  value={opt.id}
                  checked={checked}
                  onChange={e => {
                    const prev = Array.isArray(current) ? current : [];
                    const next = e.target.checked
                      ? [...prev, opt.id]
                      : prev.filter(v => v !== opt.id);
                    onChange(id, next);
                  }}
                />
                <span className="es-check-custom" />
                <span>{opt.option_text}</span>
              </label>
            );
          })}
        </div>
      );

    case 'rating': {
      const ticks = Array.from({ length: rating_max }, (_, i) => i + 1);
      return (
        <div className="es-rating-wrap">
          {(rating_label_low || rating_label_high) && (
            <div className="es-rating-labels">
              <span>{rating_label_low}</span>
              <span>{rating_label_high}</span>
            </div>
          )}
          <div className="es-rating-scale">
            {ticks.map(n => (
              <button
                key={n}
                type="button"
                className={`es-rating-btn ${current === String(n) ? 'es-rating-active' : ''}`}
                onClick={() => onChange(id, String(n))}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      );
    }

    case 'yes_no':
      return (
        <div className="es-options-list es-yes-no">
          {['Yes', 'No'].map(val => (
            <label key={val} className={`es-radio-label ${current === val ? 'es-selected' : ''}`}>
              <input
                type="radio"
                name={`q_${id}`}
                value={val}
                checked={current === val}
                onChange={() => onChange(id, val)}
              />
              <span className="es-radio-custom" />
              <span>{val}</span>
            </label>
          ))}
        </div>
      );

    case 'date':
      return (
        <input
          type="date"
          className="es-text-input"
          value={current || ''}
          onChange={e => onChange(id, e.target.value)}
        />
      );

    case 'textarea':
      return (
        <textarea
          className="es-textarea"
          rows={4}
          placeholder="Your answer…"
          value={current || ''}
          onChange={e => onChange(id, e.target.value)}
        />
      );

    case 'text':
    default:
      return (
        <input
          type="text"
          className="es-text-input"
          placeholder="Your answer…"
          value={current || ''}
          onChange={e => onChange(id, e.target.value)}
        />
      );
  }
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function EventSurvey() {
  const { id: routeSurveyId } = useParams();
  const [searchParams] = useSearchParams();
  const surveyId = routeSurveyId || searchParams.get('id');

  // Survey data
  const [survey, setSurvey] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | error | submitted

  // Respondent info
  const [respondentName, setRespondentName] = useState('');
  const [respondentEmail, setRespondentEmail] = useState('');

  // Answers: { [questionId]: value }
  const [answers, setAnswers] = useState({});

  // Validation errors: { [questionId]: string }
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError]     = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [thankYou, setThankYou]       = useState('');

  // ── Load survey ────────────────────────────────────────────────────────

  const loadSurvey = useCallback(async () => {
    if (!surveyId) {
      setLoadState('error');
      return;
    }
    setLoadState('loading');
    try {
      const res = await fetch(`${API_ENDPOINTS.BASE_URL}/surveys/${surveyId}/take`);
      const data = await res.json();
      if (res.ok && data.success) {
        setSurvey(data.data);
        setLoadState('ready');
      } else {
        setLoadState('error');
      }
    } catch {
      setLoadState('error');
    }
  }, [surveyId]);

  useEffect(() => { loadSurvey(); }, [loadSurvey]);

  // ── Answer handler ─────────────────────────────────────────────────────

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setFieldErrors(prev => ({ ...prev, [questionId]: undefined }));
  };

  // ── Validate ───────────────────────────────────────────────────────────

  const validate = () => {
    const errs = {};
    if (!survey.is_anonymous) {
      if (!respondentName.trim()) errs['_name']  = 'Name is required';
      if (!respondentEmail.trim()) errs['_email'] = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(respondentEmail)) {
        errs['_email'] = 'Please enter a valid email address';
      }
    }
    for (const q of survey.questions) {
      if (!q.is_required) continue;
      const val = answers[q.id];
      const empty =
        val === undefined ||
        val === null ||
        val === '' ||
        (Array.isArray(val) && val.length === 0);
      if (empty) errs[q.id] = 'This question is required';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Submit ─────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!validate()) {
      setFormError('Please answer all required questions before submitting.');
      return;
    }

    setSubmitting(true);
    try {
      // Build answers array for API
      const answerPayload = survey.questions
        .filter(q => answers[q.id] !== undefined && answers[q.id] !== '' && answers[q.id] !== null)
        .map(q => {
          const val = answers[q.id];
          if (q.question_type === 'checkbox') {
            return { question_id: q.id, option_ids: val };
          }
          if (['multiple_choice', 'dropdown'].includes(q.question_type)) {
            return { question_id: q.id, option_id: val };
          }
          return { question_id: q.id, answer_text: String(val) };
        });

      const body = {
        answers: answerPayload,
        ...(survey.is_anonymous ? {} : {
          respondent_name:  respondentName.trim(),
          respondent_email: respondentEmail.trim().toLowerCase(),
        }),
      };

      const res  = await fetch(`${API_ENDPOINTS.BASE_URL}/surveys/${surveyId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setThankYou(data.message || 'Thank you for completing the survey!');
        setLoadState('submitted');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (res.status === 409) {
        setFormError(data.message || 'You have already submitted a response to this survey.');
      } else {
        setFormError(data.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setFormError('Unable to submit. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render states ──────────────────────────────────────────────────────

  if (!surveyId) {
    return (
      <main className="es-page">
        <div className="es-state-card">
          <div className="es-state-icon">🔗</div>
          <h2>No Survey ID</h2>
          <p>This link is missing a survey ID. Please check the URL you were given.</p>
        </div>
      </main>
    );
  }

  if (loadState === 'loading') {
    return (
      <main className="es-page">
        <div className="es-state-card">
          <div className="es-spinner" aria-label="Loading" />
          <p>Loading survey…</p>
        </div>
      </main>
    );
  }

  if (loadState === 'error') {
    return (
      <main className="es-page">
        <SEO title="Survey Not Found" />
        <div className="es-state-card">
          <div className="es-state-icon">🚫</div>
          <h2>Survey Unavailable</h2>
          <p>This survey is not currently active, has expired, or could not be found.</p>
          <p className="es-state-hint">Please contact the organiser if you believe this is an error.</p>
        </div>
      </main>
    );
  }

  if (loadState === 'submitted') {
    return (
      <main className="es-page">
        <SEO title="Response Submitted" />
        <div className="es-state-card es-state-success">
          <div className="es-state-icon">✅</div>
          <h2>Response Submitted!</h2>
          <p className="es-thankyou-msg">{thankYou}</p>
          <p className="es-state-hint">You may now close this tab.</p>
        </div>
      </main>
    );
  }

  // ── Main form ──────────────────────────────────────────────────────────

  const totalRequired = (survey.questions || []).filter(q => q.is_required).length;
  const answeredRequired = (survey.questions || []).filter(q => q.is_required && answers[q.id] !== undefined && answers[q.id] !== '' && !(Array.isArray(answers[q.id]) && answers[q.id].length === 0)).length;
  const progress = totalRequired > 0 ? Math.round((answeredRequired / totalRequired) * 100) : 0;

  return (
    <main className="es-page">
      <SEO
        title={survey.title}
        description={survey.description || 'Please complete this survey'}
      />

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="es-hero">
        <p className="es-eyebrow">Lead With Tech · Survey</p>
        <h1 className="es-title">{survey.title}</h1>
        {survey.description && (
          <p className="es-subtitle">{survey.description}</p>
        )}
        {survey.end_date && (
          <p className="es-deadline">
            ⏱ Closes {new Date(survey.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        )}
      </section>

      {/* ── Progress bar ──────────────────────────────────────────────── */}
      <div className="es-progress-wrap" aria-label={`${progress}% complete`}>
        <div className="es-progress-bar" style={{ width: `${progress}%` }} />
      </div>

      {/* ── Form ──────────────────────────────────────────────────────── */}
      <section className="es-body">
        <form className="es-form" onSubmit={handleSubmit} noValidate>

          {/* Respondent info (shown unless anonymous) */}
          {!survey.is_anonymous && (
            <div className="es-card es-respondent-card">
              <h2 className="es-card-title">👤 Your Details</h2>
              <div className="es-field-row">
                <div className="es-field">
                  <label className="es-label">
                    Full Name <span className="es-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`es-text-input ${fieldErrors['_name'] ? 'es-input-error' : ''}`}
                    placeholder="Jane Doe"
                    value={respondentName}
                    onChange={e => { setRespondentName(e.target.value); setFieldErrors(p => ({ ...p, _name: undefined })); }}
                    autoComplete="name"
                  />
                  {fieldErrors['_name'] && <span className="es-field-error">{fieldErrors['_name']}</span>}
                </div>
                <div className="es-field">
                  <label className="es-label">
                    Email Address <span className="es-required">*</span>
                  </label>
                  <input
                    type="email"
                    className={`es-text-input ${fieldErrors['_email'] ? 'es-input-error' : ''}`}
                    placeholder="jane@example.com"
                    value={respondentEmail}
                    onChange={e => { setRespondentEmail(e.target.value); setFieldErrors(p => ({ ...p, _email: undefined })); }}
                    autoComplete="email"
                  />
                  {fieldErrors['_email'] && <span className="es-field-error">{fieldErrors['_email']}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Questions */}
          {(survey.questions || []).map((q, idx) => (
            <div
              key={q.id}
              className={`es-card es-question-card ${fieldErrors[q.id] ? 'es-card-error' : ''}`}
            >
              <div className="es-question-header">
                <span className="es-q-num">{idx + 1}</span>
                <div className="es-q-text-wrap">
                  <p className="es-q-text">
                    {q.question_text}
                    {q.is_required && <span className="es-required" aria-label="Required"> *</span>}
                  </p>
                  {q.helper_text && (
                    <p className="es-q-hint">{q.helper_text}</p>
                  )}
                </div>
              </div>

              <div className="es-question-body">
                <QuestionInput question={q} answers={answers} onChange={handleAnswer} />
              </div>

              {fieldErrors[q.id] && (
                <p className="es-field-error es-q-error">{fieldErrors[q.id]}</p>
              )}
            </div>
          ))}

          {/* Form-level error */}
          {formError && (
            <div className="es-form-error" role="alert">
              ⚠ {formError}
            </div>
          )}

          {/* Submit */}
          <div className="es-submit-row">
            <p className="es-submit-hint">
              {totalRequired > 0 && `${answeredRequired} of ${totalRequired} required question${totalRequired !== 1 ? 's' : ''} answered`}
            </p>
            <button
              type="submit"
              className="es-submit-btn"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="es-btn-spinner" />
                  Submitting…
                </>
              ) : 'Submit Response →'}
            </button>
          </div>

        </form>
      </section>
    </main>
  );
}
